/**
 * Publish Rules Service
 *
 * Interfaces with ISE backend for listing/blocking configuration.
 * Follows ISE endpoint pattern: GET for fetch, POST for create, PUT for update.
 *
 * NOTE: The shapes here are aligned with the canonical listing-rules types
 * defined in `src/types/appTypes.ts` (LRRule, LRGroup, ListingRulesConfig).
 */

import type { LRRule, LRGroup } from '@/types/appTypes'

// Reuse LRRule/LRGroup so shapes stay consistent across the app.
export type ListingRule = LRRule
export type ListingRuleGroup = LRGroup

export interface ListingRuleset {
  logic?: 'AND' | 'OR'
  rules?: Array<LRRule | LRGroup>
}

export interface ListingRulesEvaluation {
  mode?: string
  fallback_action?: 'allow'
}

// Match the app-level LRSegment.inventory_membership_rules shape (both keys optional),
// so we can assign configs directly without extra casting.
export interface ListingRulesData {
  ruleset?: ListingRuleset
  evaluation?: ListingRulesEvaluation
}

export interface IListingRulesResponse {
  success: boolean
  data: {
    jazelId: string
    inventory_membership_rules: ListingRulesData
  }
}

export interface ICreateListingRulesPayload {
  jazelId: string
  inventory_membership_rules: ListingRulesData
}

export interface IUpdateListingRulesPayload {
  inventory_membership_rules: ListingRulesData
}

/**
 * Fetches publish rules configuration from ISE backend
 * 
 * @param iseEndpoint - ISE backend endpoint
 * @param juid - Dealer account ID (jazelId)
 * @returns Promise<IListingRulesResponse | null> - Publish rules config or null if error
 */
export const fetchListingRules = async (
  iseEndpoint: string,
  juid: string,
): Promise<IListingRulesResponse | null> => {
  const base = String(iseEndpoint).replace(/\/+$/g, '')
  const path = `publish-rules-config/${encodeURIComponent(juid)}`
  const url = `${base}/${path}`

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })

    if (!response.ok) {
      console.error('Failed to fetch publish rules:', response.status, response.statusText)
      return null
    }

    const json = await response.json()
    return json
  } catch (e) {
    console.error('ise', 'fetchListingRules', e)
    return null
  }
}

/**
 * Creates initial publish rules configuration (for onboarding)
 * 
 * @param iseEndpoint - ISE backend endpoint
 * @param payload - Publish rules configuration with jazelId
 * @param token - Optional session token for authentication
 * @returns Promise<boolean> - Success status
 */
export const createListingRules = async (
  iseEndpoint: string,
  payload: ICreateListingRulesPayload,
  token: string,
): Promise<boolean> => {
  const base = String(iseEndpoint).replace(/\/+$/g, '')
  const path = `publish-rules-config`
  const url = `${base}/${path}`

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    authorization: `Bearer ${token}`,
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      console.error('Failed to create publish rules:', response.status, response.statusText)
      return false
    }

    console.log('✅ Publish rules created successfully')
    return true
  } catch (e) {
    console.error('ise', 'createListingRules', e)
    return false
  }
}

/**
 * Updates existing publish rules configuration
 * 
 * @param iseEndpoint - ISE backend endpoint
 * @param juid - Dealer account ID (jazelId)
 * @param payload - Publish rules configuration (without jazelId)
 * @param token - Optional session token for authentication
 * @returns Promise<boolean> - Success status
 */
export const updateListingRules = async (
  iseEndpoint: string,
  juid: string,
  payload: IUpdateListingRulesPayload,
  token: string,
): Promise<boolean> => {
  const base = String(iseEndpoint).replace(/\/+$/g, '')
  const path = `publish-rules-config/${encodeURIComponent(juid)}`
  const url = `${base}/${path}`

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    authorization: `Bearer ${token}`,
  }

  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      console.error('Failed to update publish rules:', response.status, response.statusText)
      return false
    }

    console.log('✅ Publish rules updated successfully')
    return true
  } catch (e) {
    console.error('ise', 'updateListingRules', e)
    return false
  }
}

